// Peppol ID check: search the Peppol Directory by UEN or company name. Used on /how-it-works and in checklist step 3.
import { useId, useRef, useState, type ReactElement } from 'react'
import { T, useT } from './i18n'
import { normaliseUen, searchPeppol, type PeppolSearch } from './peppol'
import { to } from './routes'
import { Ico, MONO, label, linkBtn } from './ui'

const text = { fontSize: 14, lineHeight: 1.5, color: '#e4eee9' }

export function PeppolChecker() {
  const { t, L } = useT()
  const id = useId()
  const [q, setQ] = useState('')
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState('')
  const [res, setRes] = useState<PeppolSearch | null>(null)
  const pending = useRef<AbortController | null>(null)
  const run = async () => {
    if (!normaliseUen(q) && q.trim().length < 2) { setErr(t('peppol.invalid')); setRes(null); return }
    pending.current?.abort()
    const ctl = pending.current = new AbortController()
    setErr(''); setRes(null); setBusy(true)
    try { setRes(await searchPeppol(q, ctl.signal)) }
    catch { if (!ctl.signal.aborted) setErr(t('peppol.unreachable')) }
    finally { if (pending.current === ctl) setBusy(false) }
  }
  const lnk = { lnk: <a href={L(to('find-vendor'))} /> }
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 4 }}>
      <label htmlFor={id} style={label}>{t('peppol.label')}</label>
      <div className="btn-stack" style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <input id={id} value={q} onChange={e => setQ(e.target.value)} onKeyDown={e => e.key === 'Enter' && run()} placeholder={t('peppol.placeholder')} inputMode="text" autoComplete="off" spellCheck={false} maxLength={100} style={{ flex: '1 1 200px', minWidth: 0, padding: '13px 14px', border: '1px solid rgba(178,245,220,0.4)', background: 'transparent', fontSize: 16, fontFamily: MONO, color: '#f2f6f4' }} />
        <button type="button" className="btn-fill" onClick={run} disabled={busy} style={{ ...linkBtn(true), border: 'none', cursor: 'pointer', fontFamily: 'inherit', fontSize: 15, opacity: busy ? 0.6 : 1 }}>{busy ? t('peppol.searching') : t('peppol.check')}</button>
      </div>
      {err && <span role="alert" style={{ fontSize: 14, color: '#f2f6f4' }}>{err}</span>}
      <div aria-live="polite">{res && <Result res={res} lnk={lnk} />}</div>
    </div>
  )
}

function Result({ res, lnk }: { res: PeppolSearch; lnk: Record<string, ReactElement> }) {
  const { t } = useT()
  const idStyle = { fontFamily: MONO, color: '#b2f5dc' }
  if (!res.matches.length) return <span style={text}><T k={res.uen ? 'peppol.notFound' : 'peppol.notFoundName'} components={lnk} /> →</span>
  if (res.uen) {
    const m = res.matches[0]
    return <span style={text}><Ico n="check_circle" size={18} /> <T k="peppol.found" values={{ name: m.name ?? t('peppol.registered'), peppolId: m.peppolId }} components={{ b: <strong />, id: <span style={idStyle} lang="en" /> }} /></span>
  }
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      <span style={text}>{t('peppol.matches', { total: res.total })}</span>
      <ul style={{ margin: 0, padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', borderTop: '1px solid rgba(178,245,220,0.18)' }}>
        {res.matches.map(m => (
          <li key={m.peppolId} style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', gap: '2px 16px', padding: '10px 0', borderBottom: '1px solid rgba(178,245,220,0.18)' }}>
            <span style={{ ...text, fontWeight: 600 }} lang="en">{m.name ?? t('peppol.registered')}</span>
            <span style={{ ...idStyle, fontSize: 13, overflowWrap: 'anywhere' }} lang="en">{m.peppolId}</span>
          </li>
        ))}
      </ul>
      {res.total > res.matches.length && <span style={{ ...text, fontSize: 13, color: '#9dbdb2' }}>{t('peppol.more', { shown: res.matches.length, total: res.total })}</span>}
    </div>
  )
}
